// All content, trademarks, and data on this document are the property of Healthworks Analytics, LLC and are protected by applicable intellectual property laws. Unauthorized use, reproduction, or distribution of this material is strictly prohibited.
import { memo, useState } from "react";
import { MdMoreHoriz } from "react-icons/md";
import { useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { LuHelpCircle, LuMaximize2, LuMinimize2 } from "react-icons/lu";
import { CloseIcon, ChevronRightIcon } from "../icons";
import HelpAndSupportContent from "./HelpAndSupportContent";
import CreateTicketPopup from "../forms/CreateTicketPopup";
import ViewTicketSection, { PanelTicketDetail } from "../sections/ViewTicketSection";

const VIEW_TITLES = {
  ticket: "Raise a Ticket",
  tickets: "My Tickets",
  ticketDetail: "View Details",
};

const BACK_VIEW = {
  ticket: "menu",
  tickets: "menu",
  ticketDetail: "tickets",
};

// Maps the current route to a human-readable page name for the "Help - <page>"
// title, mirroring the routes declared in router/Router.jsx. Exported so
// HelpAndSupportContent.jsx's "Liking this <page>?" feedback prompt can reuse
// the exact same name instead of a hardcoded "dashboard" that didn't match.
export function getCurrentPageName(pathname, clientID, activedashboard) {
  const path = (clientID ? pathname.replace(new RegExp(`^/${clientID}`), "") : pathname) || "/";

  if (path === "/" || path === "") return "Landing Page";
  if (path.startsWith("/dashboard")) return activedashboard?.dashboardname || activedashboard?.label || "Dashboard";
  if (path.startsWith("/ticket")) return "Tickets";
  if (path.startsWith("/profile")) return "Profile";
  if (path.startsWith("/whitepapers")) return "Whitepapers";
  if (path.startsWith("/subscribe")) return "Subscribe";
  if (path.startsWith("/product")) return "Product";
  return "";
}

const HelpAndSupportPanel = ({ onClose, onOpenHelpGuide, onHelpGuideHover }) => {
  const [minimized, setMinimized] = useState(false);
  const [view, setView] = useState("menu");
  const [searchedResult, setSearchedResult] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState(null);

  const location = useLocation();
  const { clientID } = useParams();
  const { activedashboard } = useSelector((state) => ({ activedashboard: state.activedashboard }));
  const pageName = getCurrentPageName(location.pathname, clientID, activedashboard);
  const helpTitle = pageName ? `Help - ${pageName}` : "Help";

  const breadcrumbTrails = {
    menu: [helpTitle],
    ticket: [helpTitle, "Raise a Ticket"],
    tickets: [helpTitle, "My Tickets"],
    ticketDetail: [helpTitle, "…", "View Details"],
  };

  if (minimized) {
    return (
      <div className="font-geist w-[350px] fixed bottom-0 right-0 z-50 pl-4 pr-3 py-3 bg-general-secondary rounded-tl-lg rounded-tr-lg shadow-[0px_-2px_12px_0px_rgba(0,0,0,0.14)] inline-flex justify-start items-center gap-3">
        <div className="flex-1 min-w-0 flex justify-start items-center gap-1">
          {breadcrumbTrails[view].map((crumb, index, trail) => {
            const isLast = index === trail.length - 1;
            return (
              <span key={`${crumb}-${index}`} className={`flex justify-start items-center gap-1 min-w-0 ${isLast ? "shrink-0" : "shrink"}`}>
                {index > 0 && <ChevronRightIcon className="size-3 text-general-muted-foreground shrink-0" />}
                {crumb === "…" ? (
                  <MdMoreHoriz className="size-4 text-general-muted-foreground shrink-0" />
                ) : (
                  <span
                    className={`min-w-0 text-sm font-medium leading-5 truncate ${
                      isLast ? "text-general-foreground" : "text-general-muted-foreground"
                    }`}
                  >
                    {crumb}
                  </span>
                )}
              </span>
            );
          })}
        </div>
        <div className="flex justify-start items-center gap-1.5">
          <button
            type="button"
            onClick={() => setMinimized(false)}
            className="min-w-7 min-h-7 p-1.5 rounded-lg hover:bg-general-accent ani flex justify-center items-center"
          >
            <LuMaximize2 className="size-3.5 text-general-foreground" />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="min-w-7 min-h-7 p-1.5 rounded-lg hover:bg-general-accent ani flex justify-center items-center"
          >
            <CloseIcon className="size-3.5 text-general-foreground" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="font-geist w-[350px] h-screen fixed right-0 top-0 bottom-0 z-50 bg-general-background shadow-[-4px_0px_16px_0px_rgba(0,0,0,0.12)] flex flex-col justify-start items-start">
      <div className="self-stretch p-3 inline-flex justify-start items-center gap-2.5">
        {VIEW_TITLES[view] ? (
          <>
            <button
              type="button"
              onClick={() => setView(BACK_VIEW[view])}
              className="min-w-7 min-h-7 p-1.5 -ml-1.5 rounded-lg hover:bg-general-accent ani flex justify-center items-center"
            >
              <ChevronRightIcon className="size-4 rotate-180 text-general-foreground" />
            </button>
            <div className="flex-1 text-left text-general-foreground text-sm font-medium leading-5">
              {VIEW_TITLES[view]}
            </div>
          </>
        ) : (
          <>
            <LuHelpCircle className="size-4 shrink-0 text-general-foreground" />
            <div className="flex-1 text-left text-general-foreground text-sm font-medium leading-5">
              {helpTitle}
            </div>
          </>
        )}
        <div className="flex justify-start items-center gap-1.5">
          <button
            type="button"
            onClick={() => setMinimized(true)}
            className="min-w-7 min-h-7 p-1.5 rounded-lg hover:bg-general-accent ani flex justify-center items-center"
          >
            <LuMinimize2 className="size-3.5 text-general-foreground" />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="min-w-7 min-h-7 p-1.5 rounded-lg hover:bg-general-accent ani flex justify-center items-center"
          >
            <CloseIcon className="size-3.5 text-general-foreground" />
          </button>
        </div>
      </div>

      <div className="self-stretch h-px bg-general-border" />

      {view === "ticket" && (
        <CreateTicketPopup
          variant="panel"
          searchedResult={searchedResult}
          setSearchedResult={setSearchedResult}
          onViewHistory={() => setView("tickets")}
        />
      )}
      {view === "tickets" && (
        <ViewTicketSection
          variant="panel"
          searchedResult={searchedResult}
          setSearchedResult={setSearchedResult}
          isPopup={false}
          onViewTicket={(ticketId) => {
            setSelectedTicketId(ticketId);
            setView("ticketDetail");
          }}
        />
      )}
      {view === "ticketDetail" && <PanelTicketDetail ticketId={selectedTicketId} />}
      {view === "menu" && (
        <HelpAndSupportContent
          onNavigate={onClose}
          onRaiseTicket={() => setView("ticket")}
          onMyTickets={() => setView("tickets")}
          onHelpGuide={() => {
            onOpenHelpGuide?.();
            onClose?.();
          }}
          onHelpGuideHover={onHelpGuideHover}
        />
      )}
    </div>
  );
};

export default memo(HelpAndSupportPanel);
